<template>
  <main class="ps-error">
    <template v-if="isNotFound">
      <div class="ps-error__stage">
        <!-- Decorative: the <h1> below says the same thing in words. The
             numerals, road and placeholder "0" are server-rendered so the
             page reads as 404 before the rest of the scene is drawn. -->
        <svg
          ref="scene"
          class="ps-error__scene"
          viewBox="0 0 1200 500"
          aria-hidden="true"
          focusable="false"
        >
          <defs>
            <clipPath id="ps-error-clip">
              <rect x="0" y="0" width="1200" height="500" />
            </clipPath>
          </defs>
          <g clip-path="url(#ps-error-clip)">
            <g data-scene="clouds" />
            <g data-scene="skyline" />
            <g class="ps-error__numerals">
              <text x="444" y="200">4</text>
              <text x="756" y="200">4</text>
            </g>
            <circle
              data-scene="ghost"
              cx="600"
              cy="122"
              r="74"
              fill="none"
              stroke="#c9cdf3"
              stroke-width="4"
              stroke-dasharray="10 12"
            />
            <g data-scene="trees" />
            <line
              x1="0"
              y1="440"
              x2="1200"
              y2="440"
              stroke="#1b2064"
              stroke-width="3"
            />
            <g data-scene="dashes" />
            <g data-scene="puffs" />
            <g data-scene="bike" />
            <g data-scene="wheel" class="ps-error__wheel" />
            <g data-scene="bubble" opacity="0">
              <circle r="20" fill="#fff" stroke="#1b2064" stroke-width="3" />
              <circle
                cx="-16"
                cy="22"
                r="5"
                fill="#fff"
                stroke="#1b2064"
                stroke-width="2.5"
              />
              <text class="ps-error__bubble-text" y="9">?</text>
            </g>
          </g>
        </svg>
        <button
          class="ps-error__replay"
          :class="{ 'is-visible': canReplay }"
          type="button"
          @click="replay"
        >
          <svg
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            aria-hidden="true"
          >
            <path d="M2.5 8a5.5 5.5 0 1 0 1.6-3.9" />
            <path d="M2.5 2.5v3h3" />
          </svg>
          Replay animation
        </button>
      </div>

      <section class="ps-error__copy">
        <h1 class="ps-error__title">We can't find that page</h1>
        <p class="ps-error__lede">
          <template v-if="displayPath">
            There's nothing at <code>{{ displayPath }}</code
            >.
          </template>
          The link might be out of date, or the address may have a typo.
          <strong>Your cover isn't affected.</strong> Let's get you back on the
          road.
        </p>
        <div class="ps-error__actions">
          <nuxt-link to="/" class="button ps-btn ps-btn--secondary">
            Go to homepage
          </nuxt-link>
          <nuxt-link
            :to="quoteHref"
            class="button ps-btn ps-btn--cta"
            data-test="error-quote"
          >
            Get your personal quote
          </nuxt-link>
        </div>

        <nav class="ps-error__shortcuts" aria-labelledby="ps-error-shortcuts">
          <h2 id="ps-error-shortcuts" class="ps-error__shortcuts-title">
            Looking for something specific?
          </h2>
          <ul>
            <li v-for="link in shortcuts" :key="link.to">
              <nuxt-link :to="link.to">{{ link.label }}</nuxt-link>
            </li>
          </ul>
        </nav>

        <p class="ps-error__help">
          Still stuck? Call us on
          <a href="tel:08008886745">0800 888 6745</a> or
          <nuxt-link to="/contact-us">get in touch</nuxt-link>.
        </p>
      </section>
    </template>

    <section v-else class="ps-error__copy ps-error__copy--generic">
      <p class="ps-error__code">Error {{ statusCode }}</p>
      <h1 class="ps-error__title">Something went wrong on our side</h1>
      <p class="ps-error__lede">
        Sorry about that. It's usually temporary, so please try again in a
        moment. <strong>Your cover isn't affected.</strong>
      </p>
      <div class="ps-error__actions">
        <button
          type="button"
          class="button ps-btn ps-btn--action"
          @click="reload"
        >
          Try again
        </button>
        <nuxt-link to="/" class="button ps-btn ps-btn--secondary">
          Go to homepage
        </nuxt-link>
      </div>
      <p class="ps-error__help">
        If it keeps happening, call us on
        <a href="tel:08008886745">0800 888 6745</a>.
      </p>
    </section>
  </main>
</template>

<script>
import { quotePath } from '@/helpers/estimator'
import { createErrorScene } from '@/helpers/error-scene'

// Past this the path is more likely junk (or a pasted token) than a typo the
// visitor could spot, and it would wreck the line length.
const MAX_DISPLAY_PATH = 60

export default {
  props: {
    error: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      canReplay: false,
    }
  },
  head() {
    return {
      title: this.isNotFound
        ? 'Page not found | Pedalsure'
        : 'Something went wrong | Pedalsure',
      meta: [
        // Error responses must never be indexed — without this a soft-404
        // can be crawled and stored like a real page.
        { hid: 'robots', name: 'robots', content: 'noindex,follow' },
      ],
      // The layout's buildSeoHead() would otherwise point the canonical at
      // the URL that doesn't exist.
      link: [{ hid: 'canonical', rel: 'canonical', skip: true }],
    }
  },
  computed: {
    statusCode() {
      return this.error.statusCode || 500
    },
    isNotFound() {
      return this.statusCode === 404
    },
    displayPath() {
      const path = this.$route.path
      return path && path !== '/' && path.length <= MAX_DISPLAY_PATH ? path : ''
    },
    quoteHref() {
      return quotePath({ src: '404' })
    },
    shortcuts() {
      return [
        { to: '/bike-insurance', label: 'Bike insurance' },
        { to: '/claim', label: 'Make a claim' },
        { to: '/login', label: 'Log in to your policy' },
        { to: '/policy-information', label: 'Policy information' },
        { to: '/faq', label: 'FAQs' },
      ]
    },
  },
  watch: {
    // The error layout stays mounted when one missing URL leads to another
    // (e.g. back/forward between two 404s); Nuxt hands over a fresh error
    // object each time. Watching the route instead would also fire on the way
    // out to a page that does exist.
    error(next) {
      if (next) this.track()
    },
  },
  mounted() {
    this.track()
    if (this.isNotFound && this.$refs.scene) {
      const reduced = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches
      this.scene = createErrorScene(this.$refs.scene, {
        reduced,
        onSettled: () => {
          this.canReplay = !reduced
        },
      })
    }
  },
  beforeDestroy() {
    if (this.scene) this.scene.destroy()
  },
  methods: {
    replay() {
      this.canReplay = false
      this.scene.replay()
    },
    reload() {
      window.location.reload()
    },
    // A dedicated event so GTM/GA can report broken inbound links (path +
    // referrer) without anyone having to filter page_view by title.
    track() {
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event: this.isNotFound ? 'page_not_found' : 'page_error',
        error_status: this.statusCode,
        error_path: this.$route.fullPath,
        error_referrer: document.referrer || '(direct)',
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.ps-error {
  background: $ps-white;
  overflow-x: hidden;
}

.ps-error__stage {
  position: relative;
  max-width: 1200px;
  margin: 8px auto 0;
  padding: 0 12px;
}

.ps-error__scene {
  display: block;
  width: 100%;
  height: auto;
}

.ps-error__numerals text {
  font-family: $family-bold;
  font-weight: 700;
  font-size: 220px;
  fill: $ps-primary;
  text-anchor: middle;
}

.ps-error__bubble-text {
  font-family: $family-bold;
  font-weight: 700;
  font-size: 26px;
  fill: $ps-primary;
  text-anchor: middle;
}

.ps-error__wheel {
  cursor: pointer;
}

.ps-error__replay {
  position: absolute;
  top: 8px;
  right: 20px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: $ps-tap-min;
  padding: 6px 10px;
  border: 0;
  border-radius: $ps-radius-sm;
  background: none;
  font-family: $family-regular;
  font-size: 14px;
  color: $ps-muted;
  cursor: pointer;
  // visibility rather than v-show: keeps its box reserved on mobile (where it
  // sits in the flow) so the copy doesn't jump when it appears, while still
  // taking it out of the tab order and accessibility tree until then.
  visibility: hidden;

  &.is-visible {
    visibility: visible;
  }

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover {
    color: $ps-primary;
  }

  &:focus-visible {
    outline: 3px solid $ps-primary;
    outline-offset: 2px;
  }
}

.ps-error__copy {
  max-width: 640px;
  margin: 0 auto;
  padding: 12px 24px 88px;
  text-align: center;

  &--generic {
    padding-top: 96px;
  }
}

.ps-error__code {
  margin-bottom: 12px;
  font-size: $ps-text-label;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: $ps-muted;
}

.ps-error__title {
  font-size: clamp(28px, 4vw, 40px);
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: -0.01em;
  color: $ps-primary;
}

.ps-error__lede {
  margin-top: 14px;
  font-size: $ps-text-body;
  line-height: 1.55;
  color: $ps-body-color;

  code {
    padding: 1px 6px;
    border-radius: 6px;
    background: $ps-primary-50;
    color: $ps-ink;
    font-size: 0.9em;
    word-break: break-all;
  }

  strong {
    color: $ps-ink;
  }
}

.ps-error__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  margin-top: 28px;
}

.ps-error__shortcuts {
  margin-top: 40px;

  ul {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 4px 22px;
    margin: 6px 0 0;
    padding: 0;
    list-style: none;
  }

  a {
    display: inline-block;
    padding: 10px 0; // 44px tap target without looking like a button
    font-weight: 600;
    color: $ps-primary;
    text-decoration: underline;
    text-decoration-color: $ps-primary-200;
    text-underline-offset: 4px;

    &:hover {
      text-decoration-color: $ps-primary;
    }
  }
}

.ps-error__shortcuts-title {
  font-size: $ps-text-small;
  font-weight: 400;
  color: $ps-body-color;
}

.ps-error__help {
  margin-top: 20px;
  font-size: $ps-text-small;
  color: $ps-body-color;

  a {
    font-weight: 600;
    color: $ps-primary;
  }
}

@media (max-width: 760px) {
  // Over the scene it would collide with the numerals at this width.
  .ps-error__replay {
    position: static;
    display: flex;
    margin: 0 auto;
  }

  .ps-error__actions .ps-btn {
    width: 100%;
  }
}
</style>
